import {
    ModalVariant,
    OperatorRoutedModal,
} from '../../ui/modal/OperatorRoutedModal.tsx';
import { OperatorAsyncLoadedSection } from '../../app/components/OperatorAsyncLoadedSection.tsx';
import { useParkingsOutletContext } from './Parkings.tsx';
import { Link, Outlet, useParams } from 'react-router';
import { NoAccessDialog } from '../../operator-logins/components/NoAccessDialog.tsx';
import { HeaderComponent } from '../../layout/components/HeaderComponent.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    BlockLabel,
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../../layout/components/form/FormLayout.tsx';
import { Box, Button, Tooltip } from '@mui/material';
import { OperatorRoutedModalContent } from '../../layout/components/OperatorRoutedModalContent.tsx';
import {
    OperatorParking,
    TerminalProvider,
} from '../../common/state/useParkingsFetch.ts';
import { providerToString, useGetOperatorAccounts } from './ParkingsList.tsx';
import { ReadOnlyTextField } from '../../ui/material-ui/ReadOnlyTextField.tsx';
import { Public, VerifiedUser } from '@mui/icons-material';
import { SingleZoneView } from '../../zones/components/ZoneMapView.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { urlWithOperatorExternalId } from '../../utils/Utils.ts';

export function ParkingsDetail() {
    const urlParams = useParams<{ zoneId: string }>();
    const zoneId = urlParams.zoneId ? parseInt(urlParams.zoneId, 10) : null;

    const { parkingsState } = useParkingsOutletContext();

    return (
        <OperatorRoutedModal
            variant={ModalVariant.BIG}
            render={controller => (
                <OperatorAsyncLoadedSection
                    requestState={parkingsState}
                    render={parkings => {
                        const parking = parkings.find(
                            parking => parking.zoneId === zoneId,
                        );

                        if (parking) {
                            return (
                                <Details
                                    parking={parking}
                                    onClose={controller.close}
                                />
                            );
                        } else {
                            return (
                                <NoAccessDialog
                                    open
                                    onClose={controller.close}
                                />
                            );
                        }
                    }}
                />
            )}
        />
    );
}

function Details({
    parking,
    onClose,
}: {
    parking: OperatorParking;
    onClose: () => void;
}) {
    const { operatorExternalId } = useOperatorContext();

    const accounts = useGetOperatorAccounts();
    const account = accounts.find(
        account => account.dtaAccountId === parking.dtaAccountId,
    );
    const showAccount = accounts.length > 1;

    return (
        <>
            <OperatorRoutedModalContent
                header={
                    <HeaderComponent
                        title={parking.name}
                        editMode={false}
                        buttons={
                            <>
                                {parking.terminalProvider ===
                                    TerminalProvider.CLOUD_CONNECTOR &&
                                    parking.showSmartCall && (
                                        <Link to={`smart-call/profiles`}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                            >
                                                SmartCall
                                            </Button>
                                        </Link>
                                    )}
                                {parking.hasLprReport && (
                                    <a
                                        href={urlWithOperatorExternalId(
                                            `/ui-api/operator-account/parkings/${parking.zoneId}/lpr-report`,
                                            operatorExternalId,
                                        )}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <Tooltip
                                            title={
                                                <Localized
                                                    de="Bericht über die Erkennungsrate von Kennzeichenerkennung"
                                                    fr="Rapport sur le taux de reconnaissance des plaques d'immatriculation"
                                                    it="Resoconto sul tasso di riconoscimento delle targhe"
                                                    en="Report on the recognition rate of number plate recognition"
                                                />
                                            }
                                            placement="bottom"
                                        >
                                            <Button variant="outlined">
                                                <Localized
                                                    de="LPR-Rapport"
                                                    fr="Rapport LPR"
                                                    it="Rapporto LPR"
                                                    en="LPR report"
                                                />
                                            </Button>
                                        </Tooltip>
                                    </a>
                                )}
                                <Button variant="outlined" onClick={onClose}>
                                    <Localized
                                        de="Schliessen"
                                        fr="Fermer"
                                        it="Chiudi"
                                        en="Close"
                                    />
                                </Button>
                            </>
                        }
                    />
                }
                body={
                    <FormContentLayout>
                        <FormLeftColumn>
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Parkingname"
                                        fr="Nom parking"
                                        it="Nome parcheggio"
                                        en="Parking name"
                                    />
                                }
                                value={parking.name}
                            />
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Ort"
                                        fr="Localité"
                                        it="Località"
                                        en="Location"
                                    />
                                }
                                value={`${parking.zipCode} ${parking.city}`}
                            />
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Anlagetyp"
                                        fr="Type d'installation"
                                        it="Tipo d'impianto"
                                        en="Facility type"
                                    />
                                }
                                value={providerToString(
                                    parking.terminalProvider,
                                )}
                            />
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="Zutritt"
                                        fr="Accès"
                                        it="Accesso"
                                        en="Access"
                                    />
                                }
                                value={
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {parking.isPublic ? (
                                            <>
                                                <Public sx={iconSx} />
                                                <Localized
                                                    de="Öffentlich"
                                                    fr="Public"
                                                    it="Pubblico"
                                                    en="Public"
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <VerifiedUser sx={iconSx} />
                                                <Localized
                                                    de="Privat"
                                                    fr="Privé"
                                                    it="Privato"
                                                    en="Private"
                                                />
                                            </>
                                        )}
                                    </Box>
                                }
                            />
                            {parking.terminalProvider !=
                                TerminalProvider.CLOUD_CONNECTOR && (
                                <ReadOnlyTextField
                                    label={
                                        <Localized
                                            de="Terminal-ID"
                                            fr="ID terminal"
                                            it="ID terminale"
                                            en="Terminal ID"
                                        />
                                    }
                                    value={parking.terminalId}
                                />
                            )}
                        </FormLeftColumn>
                        <FormRightColumn>
                            {!!parking.geodataText && (
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '400px',
                                    }}
                                >
                                    <SingleZoneView
                                        zone={{
                                            id: parking.zoneId,
                                            externalName: parking.name,
                                            geodataText: parking.geodataText,
                                        }}
                                    />
                                </Box>
                            )}
                            <ReadOnlyTextField
                                label={
                                    <Localized
                                        de="MWST-Nummer"
                                        fr="Numéro de TVA"
                                        it="Numero IVA"
                                        en="VAT number"
                                    />
                                }
                                value={parking.vatNumber ?? EmDash}
                            />
                            {showAccount && (
                                <ReadOnlyTextField
                                    label={
                                        <Localized
                                            de="Gruppe"
                                            fr="Groupe"
                                            it="Gruppo"
                                            en="Group"
                                        />
                                    }
                                    value={account?.dtaText ?? EmDash}
                                />
                            )}
                            <Box
                                sx={{
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'end',
                                }}
                            >
                                <BlockLabel label="ID">
                                    {parking.zoneId}
                                </BlockLabel>
                            </Box>
                        </FormRightColumn>
                    </FormContentLayout>
                }
            />
            <Outlet />
        </>
    );
}

const iconSx = {
    height: 19,
    paddingRight: 0.5,
    marginTop: -0.5,
};
